import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  TextField,
  CircularProgress,
  IconButton,
  InputAdornment,
  Typography,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@material-ui/core";
import { Add, Edit, LinkOff, Delete } from "@material-ui/icons";
import { Autocomplete } from "@material-ui/lab";
import api from "../../services/api";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import { HexColorPicker } from "react-colorful";
import ConfirmationModal from "../ConfirmationModal";

const TagSelect = ({ contactId, onChange }) => {
  const [allTags, setAllTags] = useState([]);
  const [contactTags, setContactTags] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTagToEdit, setSelectedTagToEdit] = useState(null);
  const [editTagName, setEditTagName] = useState("");
  const [editTagColor, setEditTagColor] = useState("");
  const [openConfirm, setOpenConfirm] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  // Cargar todos los tags de la compañía
  useEffect(() => {
    const fetchTags = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/tags");
        setAllTags(data);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchTags();
  }, []);

  // Cargar tags del contacto específico
  useEffect(() => {
    if (!contactId) return;
    const fetchContactTags = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/contacts/${contactId}/tags`);
        setContactTags(data);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchContactTags();
  }, [contactId]);

  // Manejar asignación y remoción de tags
  const handleTagChange = async (event, newValues, reason) => {
    if (reason === "select-option") {
      const tagsToAdd = newValues.filter(
        (tag) => !contactTags.some((ct) => ct.id === tag.id)
      );
      if (tagsToAdd.length > 0) {
        try {
          const tag = tagsToAdd[0];
          await api.post(`/contacts/${contactId}/tags/${tag.id}`);
          setContactTags(newValues);
          onChange(newValues.map((t) => t.id));
          toast.success("Tag asignado correctamente.");
        } catch (err) {
          toastError(err);
        }
      }
    } else if (reason === "remove-option") {
      const tagsToRemove = contactTags.filter(
        (tag) => !newValues.some((nv) => nv.id === tag.id)
      );
      if (tagsToRemove.length > 0) {
        const tag = tagsToRemove[0];
        try {
          await api.delete(`/contacts/${contactId}/tags/${tag.id}`);
          setContactTags(newValues);
          onChange(newValues.map((t) => t.id));
          toast.success("Tag removido correctamente.");
        } catch (err) {
          toastError(err);
        }
      }
    }
  };

  // Manejar la creación de un nuevo tag y su asignación
  const handleAddTag = async () => {
    const newTagName = inputValue.trim();
    if (!newTagName) return;

    const existingTag = allTags.find(
      (t) => t.name.toLowerCase() === newTagName.toLowerCase()
    );

    if (existingTag) {
      // Si el tag ya existe, vincularlo al contacto si aún no está vinculado
      if (!contactTags.some((ct) => ct.id === existingTag.id)) {
        try {
          await api.post(`/contacts/${contactId}/tags/${existingTag.id}`);
          const updatedContactTags = [...contactTags, existingTag];
          setContactTags(updatedContactTags);
          onChange(updatedContactTags.map((t) => t.id));
          toast.success("Tag asignado correctamente.");
          setInputValue(""); // Limpiar el input después de asignar
        } catch (err) {
          toastError(err);
        }
      } else {
        toast.info("El tag ya está asignado al contacto.");
      }
    } else {
      // Si el tag no existe, crearlo y asignarlo
      try {
        setCreating(true);
        const { data: newTag } = await api.post("/tags", {
          name: newTagName,
          color: "#CCCCCC",
        });

        setAllTags((prev) => [...prev, newTag]);
        await api.post(`/contacts/${contactId}/tags/${newTag.id}`);
        const updatedContactTags = [...contactTags, newTag];
        setContactTags(updatedContactTags);
        onChange(updatedContactTags.map((t) => t.id));
        toast.success("Tag creado y asignado al contacto.");

        setSelectedTagToEdit(newTag);
        setEditTagName(newTag.name);
        setEditTagColor(newTag.color);
        setOpenDialog(true);
        setInputValue("");
      } catch (err) {
        toastError(err);
      } finally {
        setCreating(false);
      }
    }
  };
  
  // Guardar los cambios del tag (creado o editado)
  const handleSaveTagEdit = async () => {
    if (!selectedTagToEdit) return;
    try {
      setLoading(true);
      const { data } = await api.put(`/tags/${selectedTagToEdit.id}`, {
        name: editTagName,
        color: editTagColor,
      });

      // Actualizar los tags en ambos estados
      setAllTags((prev) =>
        prev.map((tag) => (tag.id === data.id ? data : tag))
      );
      setContactTags((prev) =>
        prev.map((tag) => (tag.id === data.id ? data : tag))
      );

      setOpenDialog(false);
      toast.success("Tag actualizado correctamente.");
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  // Manejar la eliminación del tag de forma permanente
  const handleDeleteTag = async () => {
    if (!selectedTagToEdit) return;
    try {
      await api.delete(`/tags/${selectedTagToEdit.id}`);
      setOpenConfirm(false);
      setOpenDialog(false);

      setAllTags((prev) => prev.filter((t) => t.id !== selectedTagToEdit.id));
      setContactTags((prev) => prev.filter((t) => t.id !== selectedTagToEdit.id));
      toast.success("Tag eliminado correctamente.");
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenEditDialog = (tag) => {
    setSelectedTagToEdit(tag);
    setEditTagName(tag.name);
    setEditTagColor(tag.color);
    setOpenDialog(true);
  };

  // Handler para desvincular un tag del contacto
  const handleUnlinkTag = async (tag) => {
    try {
      await api.delete(`/contacts/${contactId}/tags/${tag.id}`);
      setContactTags((prev) => prev.filter((t) => t.id !== tag.id));
      onChange(contactTags.filter((t) => t.id !== tag.id).map((t) => t.id));
      toast.success("Tag desvinculado del contacto correctamente.");
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Box mb={2}>
      <Autocomplete
        multiple
        value={contactTags}
        onChange={handleTagChange}
        inputValue={inputValue}
        onInputChange={(e, newInputValue) => setInputValue(newInputValue)}
        options={allTags}
        getOptionLabel={(option) => option.name || ""}
        getOptionSelected={(option, value) => option.id === value.id}
        loading={loading}
        noOptionsText="No hay opciones"
        loadingText="Cargando..."
        disableCloseOnSelect
        renderOption={(option) => (
          <Box display="flex" justifyContent="space-between" width="100%">
            <Chip
              size="small"
              label={option.name}
              style={{
                backgroundColor: option.color,
                color: "white",
                marginRight: 8,
              }}
            />
            <Box>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenEditDialog(option);
                }}
              >
                <Edit fontSize="small" />
              </IconButton>
              {contactTags.some(contactTag => contactTag.id === option.id) && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUnlinkTag(option);
                  }}
                >
                  <LinkOff fontSize="small" />
                </IconButton>
              )}
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedTagToEdit(option);
                  setOpenConfirm(true);
                }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              key={option.id}
              label={option.name}
              style={{
                backgroundColor: option.color,
                color: "white",
              }}
              onKeyDown={(e) => {
                if (e.key === "Backspace") {
                  e.preventDefault();
                }
              }}
              onClick={() => handleOpenEditDialog(option)}
              {...getTagProps({ index })}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            label="Tags"
            margin="dense"
            fullWidth
            onKeyDown={(e) => {
              if (e.key === "Enter" && inputValue.trim()) {
                e.preventDefault();
                handleAddTag();
              }
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {(loading || creating) && <CircularProgress size={20} />}
                  <InputAdornment position="end">
                    {inputValue.trim() &&
                      !contactTags.some(
                        (t) => t.name.toLowerCase() === inputValue.toLowerCase()
                      ) && (
                        <IconButton
                          size="small"
                          onClick={handleAddTag}
                          disabled={creating}
                        >
                          <Add />
                        </IconButton>
                      )}
                  </InputAdornment>
                </>
              ),
            }}
          />
        )}
      />
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Editar Tag</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nombre del Tag"
            type="text"
            fullWidth
            value={editTagName}
            onChange={(e) => setEditTagName(e.target.value)}
          />
          <Box mt={2}>
            <Typography variant="body2" gutterBottom>
              Color:
            </Typography>
            <HexColorPicker
              color={editTagColor}
              onChange={setEditTagColor}
              style={{ width: "100%" }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmAction(() => handleDeleteTag);
              setOpenConfirm(true);
            }}
            color="secondary"
            startIcon={<Delete />}
          >
            Eliminar
          </Button>
          <Button onClick={() => setOpenDialog(false)} color="primary">
            Cancelar
          </Button>
          <Button
            onClick={handleSaveTagEdit}
            color="primary"
            variant="contained"
            disabled={loading}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
      
      <ConfirmationModal
        title="Eliminar Tag"
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        onConfirm={handleDeleteTag}
      >
        {selectedTagToEdit && (
          <p>
            ¿Estás seguro de que deseas eliminar el tag "{selectedTagToEdit.name}" de forma permanente? Esta acción no se puede revertir.
          </p>
        )}
      </ConfirmationModal>
    </Box>
  );
};

TagSelect.propTypes = {
  contactId: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default TagSelect;